import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketingHeader } from '@/components/layout/MarketingHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SUPPORT_EMAIL } from '@/config/brandConfig';
import { ArrowLeft, CheckCircle2, Send } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { pageTranslations } from '@/i18n/pageTranslations';

const MR = { fontFamily: 'Manrope, sans-serif' };
const API = process.env.REACT_APP_BACKEND_URL;

export default function BriefingPage() {
  const { lang } = useLanguage();
  const b = (pageTranslations[lang] || pageTranslations.en).briefing;

  const [form, setForm] = useState({ name: '', email: '', organization: '', role: '', evaluate: '', notes: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <MarketingHeader />

      <main className="flex-1 flex items-center justify-center px-4 pt-28 pb-16 sm:pt-32 sm:pb-20">
        <div className="w-full max-w-lg">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> {b.backLink}
          </Link>

          {submitted ? (
            <Card className="bg-card border-border/50" data-testid="briefing-success">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-[hsl(150,50%,45%)]/10 border border-[hsl(150,50%,45%)]/20 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7 text-[hsl(150,50%,45%)]" />
                </div>
                <h2 className="text-xl font-bold text-foreground" style={MR}>{b.successTitle}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                  {b.successText}
                </p>
                <Link to="/">
                  <Button variant="outline" size="sm" className="mt-2 rounded-lg">{b.successBtn}</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={MR} data-testid="briefing-heading">
                  {b.heading}
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {b.subtitle}
                </p>
              </div>

              <Card className="bg-card/60 border-border/40">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="briefing-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{b.nameLabel}</label>
                        <Input value={form.name} onChange={set('name')} required placeholder={b.namePlaceholder} className="bg-background border-border/50 rounded-lg" data-testid="briefing-name" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{b.emailLabel}</label>
                        <Input type="email" value={form.email} onChange={set('email')} required placeholder={b.emailPlaceholder} className="bg-background border-border/50 rounded-lg" data-testid="briefing-email" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{b.orgLabel}</label>
                        <Input value={form.organization} onChange={set('organization')} required placeholder={b.orgPlaceholder} className="bg-background border-border/50 rounded-lg" data-testid="briefing-org" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{b.roleLabel}</label>
                        <Input value={form.role} onChange={set('role')} placeholder={b.rolePlaceholder} className="bg-background border-border/50 rounded-lg" data-testid="briefing-role" />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{b.evaluateLabel}</label>
                      <select
                        value={form.evaluate}
                        onChange={set('evaluate')}
                        required
                        className="w-full h-10 rounded-lg border border-border/50 bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="briefing-evaluate"
                      >
                        <option value="">{b.evaluateDefault}</option>
                        {b.evaluateOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider" style={MR}>{b.notesLabel}</label>
                      <textarea
                        value={form.notes}
                        onChange={set('notes')}
                        rows={3}
                        placeholder={b.notesPlaceholder}
                        className="w-full rounded-lg border border-border/50 bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        data-testid="briefing-notes"
                      />
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer" data-testid="briefing-consent-label">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        required
                        className="mt-0.5 h-4 w-4 rounded border-border/50 accent-primary flex-shrink-0"
                        data-testid="briefing-consent"
                      />
                      <span className="text-xs text-muted-foreground leading-relaxed">
                        {b.consent}{' '}
                        <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline">{b.consentLink}</Link>
                        {' '}{b.consentEnd}
                      </span>
                    </label>

                    {error && (
                      <p className="text-sm text-destructive" data-testid="briefing-error">{error}</p>
                    )}

                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11 rounded-lg font-medium" disabled={submitting || !consent} data-testid="briefing-submit">
                      {submitting ? b.submitting : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {b.submit}
                        </>
                      )}
                    </Button>

                    <p className="text-[11px] text-center text-muted-foreground/50">
                      {b.responseNote} {SUPPORT_EMAIL}.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
